<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="smallButton" icon v-on="on" :disabled="disabled">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>Add New
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{title}}</span>
      </v-card-title>
      <v-container v-if="workingSettings">
        <v-form ref="form">
          <v-row v-if="indicators">
            <v-col cols="4">
              <v-container fluid class="edit-container">
                <v-row>
                  <v-col>
                    <v-subheader class="ma-0 pa-0">Status Indicator</v-subheader>
                    <v-checkbox
                      v-for="item in indicators"
                      v-bind:key="item.enumValue"
                      class="text-body-2 ma-0 pa-0"
                      :label="item.label"
                      v-model="selectedIndicators"
                      :value="item"
                      :dense="true"
                      :loading="loading"
                      :hide-details="true"
                      :id="`checkbox-${item.enumValue}`"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    class="v-messages theme--light error--text custom-error mt-0 pt-0"
                    id="status-error"
                    v-if="statusIndicatorMissing && isDirty"
                  >Status Indicator selection is required</v-col>
                </v-row>
              </v-container>
            </v-col>
            <v-col cols="5">
              <v-container fluid>
                <v-row>
                  <v-col>
                    <v-subheader class="ma-0 pa-0">Conditional Logic</v-subheader>
                    <v-radio-group
                      v-model="condition"
                      :loading="loading"
                      :dense="true"
                      :hide-details="true"
                      :mandatory="false"
                      :disabled="selectedIndicators.length < 2"
                    >
                      <v-radio label="And" value="AND" />
                      <v-radio label="Or" value="OR" />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    class="v-messages theme--light error--text mt-0 pt-0 custom-error"
                    id="condtional-error"
                    v-if="conditionalLogicMissing"
                  >Conditional Logic is required when more than 1 indicator is selected</v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
          <v-row class="spacer-row"></v-row>
          <v-row>
            <v-col cols="12">
              <v-combobox
                :label="statusCodeLabel"
                ref="statusCodes"
                :disabled="!statusCodeRequired"
                multiple
                return-object
                append-icon
                chips
                :hide-details="true"
                deletable-chips
                v-model="workingSettings.statusCodes"
                @input="updateStatusCodes"
                :loading="loading"
              ></v-combobox>
              <v-col
                class="v-messages theme--light error--text ma-0 pa-0 mt-2 custom-error"
                id="statuscodes-error"
                v-if="statusCodesMissing && isDirty"
              >At least one Status Code is required</v-col>
              <v-col
                class="v-messages theme--light error--text ma-0 pa-0 mt-2 custom-error"
                id="statuscodes-nonunique"
                v-if="statusCodeConflict"
              >Status codes must be unique</v-col>
            </v-col>
          </v-row>

          <v-row v-if="productCodeLabel && productCodeLabel.length > 0" class="mt-8">
            <v-col cols="12">
              <v-combobox
                :label="productCodeLabel"
                ref="productCodes"
                :disabled="!productCodeRequired"
                multiple
                return-object
                append-icon
                chips
                :hide-details="true"
                deletable-chips
                v-model="workingSettings.productCodes"
                @input="updateProductCodes"
                :loading="loading"
              ></v-combobox>
              <v-col
                class="v-messages theme--light error--text ma-0 pa-0 mt-2 custom-error"
                id="productcodes-error"
                v-if="productCodesMissing && isDirty"
              >At least one Product Code is required</v-col>
              <v-col
                class="v-messages theme--light error--text ma-0 pa-0 mt-2 custom-error"
                id="productcodes-nonunique"
                v-if="productCodeConflict"
              >Product codes must be unique</v-col>
            </v-col>
          </v-row>


        </v-form>
      </v-container>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn large @click="cancel" :disabled="saving">Cancel</v-btn>
        <v-btn
          id="saveButton"
          large
          class="blue white--text"
          @click="save"
          :loading="saving"
          :disabled="!isFormValid"
        >Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import {
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import {
  StatusIndicator, StatusSettings, StatusSettingsResource,
} from '@/api/accountstatussettings/';

@Component({ computed: mapState(['fiId', 'configVersion']) })
export default class StatusSettingsDialog extends Vue {
  @Prop({ default: true })
  smallButton!: boolean;

  @Prop({ default: true })
  disabled!: boolean;

  @Prop()
  settings!: StatusSettings;

  @Prop()
  indicators!: StatusIndicator[];

  @Prop({
    default() {
      return new Set<string>();
    },
  })
  allStatusCodes!: Set<string>;

  @Prop({
    default() {
      return new Set<string>();
    },
  })
  allProductCodes!: Set<string>;

  @Prop({ default: '' })
  title!: string;

  @Prop({ default: '' })
  statusCodeLabel!: string;

  @Prop({ default: '' })
  productCodeLabel!: string;

  @Prop()
  settingsResource!: StatusSettingsResource;

  fiId!: string;

  configVersion!: string;

  originalStatusCodes: Set<string> = new Set();

  originalProductCodes: Set<string> = new Set();

  condition: string | null = null;

  selectedIndicators: StatusIndicator[] = [];

  statusIndicatorMissing: boolean = false;

  statusCodeRequired: boolean = false;

  productCodeRequired: boolean = false;

  conditionalLogicMissing: boolean = false;

  loading: boolean = false;

  dialog: boolean = false;

  saving: boolean = false;

  workingSettings: StatusSettings | null = null;

  isDirty: boolean = false;

  statusCodesMissing: boolean = false;

  statusCodeConflict: boolean = false;

  productCodesMissing: boolean = false;

  productCodeConflict: boolean = false;

  isFormValid: boolean = false;

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.loading = true;
      this.saving = false;

      if (!this.settings) {
        this.settings = this.settingsResource.createEmptySettings();
      }

      this.resetUsing(this.settings);

      this.loading = false;
      this.isDirty = false;
      this.conditionalLogicMissing = false;
      this.statusIndicatorMissing = false;
      this.statusCodesMissing = false;
      this.productCodesMissing = false;
    }
  }

  @Watch('configVersion')
  @Watch('fiId')
  onFiOrVersionChange() {
    this.dialog = false;
  }

  cancel() {
    this.dialog = false;
  }

  save() {
    // Force the combo to lose focus so that changes aren't lost
    let combo: any = this.$refs.statusCodes;
    combo.blur();

    combo = this.$refs.productCodes;
    if (combo) {
      combo.blur();
    }

    this.$nextTick(() => {
      this.validateForm();
      if (this.isFormValid) {
        this.saving = true;

        if (this.workingSettings!.active === false) {
            this.workingSettings!.active = true;
        }

        // call backing service - wait two ticks to allow user changes to be updated
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.settingsResource.save(this.fiId, this.workingSettings!).then(
              (savedSettings: StatusSettings) => {
                if (this.settings) {
                  Object.assign(this.settings, savedSettings);
                }
                this.resetUsing(savedSettings);
                this.$emit('success', savedSettings);
                this.dialog = false;
                this.saving = false;
              },
            ).catch((error: any) => {
              this.showError();
              this.saving = false;
            });
          });
        });
      }
    });
  }

  @Watch('condition')
  @Watch('selectedIndicators')
  @Watch('workingSettings', { deep: true })
  validateForm() {
    this.isDirty = true;
    this.validateStatusCodes();
    this.validateProductCodes();
    this.statusIndicatorMissing = this.indicators && this.indicators.length > 0 && this.selectedIndicators.length === 0;
    this.conditionalLogicMissing = this.selectedIndicators.length > 1 && this.condition === null;

    this.isFormValid = !this.statusCodeConflict && !this.statusIndicatorMissing && !this.conditionalLogicMissing && !this.statusCodesMissing && !this.productCodesMissing && !this.productCodeConflict;
    if (this.isFormValid) {
      this.updateStatusEnum();
    }
  }

  validateStatusCodes() {
    this.statusCodesMissing = this.statusCodesRequired() && (!this.workingSettings!.statusCodes || this.workingSettings!.statusCodes.length === 0);
    this.checkForStatusCodeConflict();
  }


  statusCodesRequired() : boolean {
    let required = true;
    if (this.indicators && this.indicators.length > 0) {
      required = false;
      for (let i = 0; i < this.selectedIndicators.length; i++) {
        if (this.selectedIndicators[i].statusCodeRequired) {
          required = this.selectedIndicators[i].statusCodeRequired;
          break;
        }
      }
    }
    this.statusCodeRequired = required;
    return required;
  }

  checkForStatusCodeConflict() {
    if (this.workingSettings!.statusCodes) {
      for (let i = 0; i < this.workingSettings!.statusCodes.length; i++) {
        const code: string = this.workingSettings!.statusCodes[i];
        if (
          !this.originalStatusCodes.has(code)
        && this.allStatusCodes.has(code)
        ) {
          this.statusCodeConflict = true;
          return;
        }
      }
    }
    this.statusCodeConflict = false;
  }

  validateProductCodes() {
    this.productCodesMissing = this.productCodesRequired() && (!this.workingSettings!.productCodes || this.workingSettings!.productCodes.length === 0);
    this.checkForProductCodeConflict();
  }

  productCodesRequired() : boolean {
    let required = false;
    if (this.indicators && this.indicators.length > 0) {
      for (let i = 0; i < this.selectedIndicators.length; i++) {
        if (this.selectedIndicators[i].productCodeRequired) {
          required = this.selectedIndicators[i].productCodeRequired;
          break;
        }
      }
    }
    this.productCodeRequired = required;
    return required;
  }

  checkForProductCodeConflict() {
    if (this.workingSettings?.productCodes) {
      for (let i = 0; i < this.workingSettings!.productCodes.length; i++) {
        const code: string = this.workingSettings!.productCodes[i];
        if (
          !this.originalProductCodes.has(code)
        && this.allProductCodes.has(code)
        ) {
          this.productCodeConflict = true;
          return;
        }
      }
    }
    this.productCodeConflict = false;
  }

  resetUsing(settings: StatusSettings) {
    this.workingSettings = { ...settings };

    this.originalStatusCodes = new Set<string>();
    if (settings.statusCodes) {
      settings.statusCodes.forEach((item: string) => {
        this.originalStatusCodes.add(item);
      });
    }

    this.originalProductCodes = new Set<string>();
    if (settings.productCodes) {
      settings.productCodes.forEach((item: string) => {
        this.originalProductCodes.add(item);
      });
    }

    if (this.indicators) {
      this.selectedIndicators = [];
      const indicatorVals = this.settingsResource.getIndicatorsFromSettings(settings);
      if (indicatorVals) {
        indicatorVals.forEach((val:string) => {
          for (let i = 0; i < this.indicators.length; i++) {
            if (this.indicators[i].enumValue === val) {
              this.selectedIndicators.push(this.indicators[i]);
              break;
            }
          }
        });
      }
    }

    this.condition = this.settingsResource.getConditionalFromSettings(settings);
  }

  updateStatusCodes() {
    this.$nextTick(() => {
      if (this.workingSettings!.statusCodes.length > 0) {
        const lastEntered: string | undefined = this.workingSettings!.statusCodes.pop();
        this.workingSettings!.statusCodes.push(...lastEntered!.split(','));
      }
      this.validateForm();
    });
  }

  updateProductCodes() {
    this.$nextTick(() => {
      if (this.workingSettings!.productCodes && this.workingSettings!.productCodes.length > 0) {
        const lastEntered: string | undefined = this.workingSettings!.productCodes!.pop();
        this.workingSettings!.productCodes.push(...lastEntered!.split(','));
      }
      this.validateForm();
    });
  }

  updateStatusEnum() {
    const indicatorVals:string[] = [];

    this.selectedIndicators.forEach((selected:StatusIndicator) => {
      indicatorVals.push(selected.enumValue);
    });

    this.settingsResource.setIndicatorsOnSettings(this.workingSettings!, indicatorVals, this.condition === 'OR');
  }

  showError() {
    const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
